import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Slider,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { updateEdge } from '../features/networkUndoSlice';
import { setSelectedEdge } from '../features/networkSlice';

export default function EditEdgeDialog() {
  const dispatch = useDispatch();
  const selectedEdge = useSelector((state) => state.network.selectedEdge);
  const nodes = useSelector((state) => state.networkUndo.present.nodes);

  const [formData, setFormData] = useState({});

  useEffect(() => setFormData(selectedEdge), [selectedEdge]);

  const reset = () => {
    dispatch((setSelectedEdge(null)));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'arrows.to.type') {
      setFormData({ ...formData, arrows: { to: { type: value } } });
    } else if (name === 'dashes') {
      setFormData({ ...formData, dashes: e.target.checked });
    } else if (name === 'smooth.type') {
      setFormData({ ...formData, smooth: { ...formData.smooth, type: value } });
    } else if (name === 'smooth.roundness') {
      setFormData({ ...formData, smooth: { ...formData.smooth, roundness: value } });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSave = () => {
    dispatch(updateEdge(formData));
    reset();
  };

  return (
    <Dialog open={selectedEdge !== null} onClose={reset} fullWidth maxWidth="md">
      {formData && (
        <form
          autoComplete="off"
          action="/"
          method="POST"
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
        >
          <DialogTitle>Edit Link</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <FormControl sx={{ m: 1 }} size="small">
                <InputLabel id="shape-select">From</InputLabel>
                <Select
                  name="from"
                  labelId="type-select"
                  value={formData.from || ''}
                  label="From"
                  onChange={handleChange}
                >
                  {nodes.map((node) => (
                    <MenuItem value={node.id} key={`from-${node.id}`}>
                      {`${node.label} (${node.id})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ m: 1 }} size="small">
                <InputLabel id="shape-select">To</InputLabel>
                <Select
                  name="to"
                  labelId="type-select"
                  value={formData.to || ''}
                  label="To"
                  onChange={handleChange}
                >
                  {nodes.map((node) => (
                    <MenuItem value={node.id} key={`to-${node.id}`}>
                      {`${node.label} (${node.id})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.from === formData.to && (
                <FormControl sx={{ m: 1 }} size="small">
                  <InputLabel shrink htmlFor="self-reference-angle-slider">
                    Self Effect Angle
                  </InputLabel>
                  <Slider
                    name="selfReference.angle"
                    id="self-reference-angle-slider"
                    value={Math.round(((formData.selfReference?.angle || 0) * 180) / Math.PI)}
                    min={0}
                    max={360}
                    step={5}
                    onChange={(e, value) => {
                      const angle = (value * Math.PI) / 180;
                      setFormData({
                        ...formData,
                        selfReference: { ...formData.selfReference, angle },
                      });
                    }}
                    valueLabelDisplay="off"
                    marks={[
                      { value: 0, label: 'E' },
                      { value: 90, label: 'N' },
                      { value: 180, label: 'W' },
                      { value: 270, label: 'S' },
                    ]}
                    aria-labelledby="self-reference-angle-slider"
                  />
                </FormControl>
              )}
              <TextField
                fullWidth
                size="small"
                multiline
                minRows="3"
                maxRows="10"
                value={formData.title || ''}
                name="title"
                label="Title"
                onChange={handleChange}
              />
              <FormControl sx={{ m: 1 }} size="small">
                <InputLabel id="shape-select">Type</InputLabel>
                <Select
                  name="arrows.to.type"
                  labelId="type-select"
                  value={formData.arrows?.to?.type || ''}
                  label="Type"
                  onChange={handleChange}
                >
                  <MenuItem value="circle">Negative</MenuItem>
                  <MenuItem value="triangle">Positive</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={formData.dashes || false}
                    name="dashes"
                    onChange={handleChange}
                  />
                )}
                label="Dashes"
              />
              <FormControl sx={{ m: 1 }} size="small">
                <InputLabel id="smooth-type-select">Type</InputLabel>
                <Select
                  name="smooth.type"
                  value={formData.smooth?.type || 'straight'}
                  label="Type"
                  onChange={handleChange}
                >
                  <MenuItem value="straight">Straight</MenuItem>
                  <MenuItem value="dynamic">Dynamic</MenuItem>
                  <MenuItem value="continuous">Continuous</MenuItem>
                  <MenuItem value="discrete">Discrete</MenuItem>
                  <MenuItem value="diagonalCross">Diagonal Cross</MenuItem>
                  <MenuItem value="straightCross">Straight Cross</MenuItem>
                  <MenuItem value="horizontal">Horizontal</MenuItem>
                  <MenuItem value="vertical">Vertical</MenuItem>
                  <MenuItem value="curvedCW">Curved CW</MenuItem>
                  <MenuItem value="curvedCCW">Curved CCW</MenuItem>
                  <MenuItem value="cubicBezier">Cubic Bezier</MenuItem>
                </Select>
              </FormControl>
              {formData.smooth?.type !== 'straight' && (
                <FormControl sx={{ m: 1 }} size="small">
                  <InputLabel shrink htmlFor="smooth-roundness-slider">Roundness</InputLabel>
                  <Slider
                    name="smooth.roundness"
                    id="smooth-roundness-slider"
                    value={formData.smooth?.roundness || 0}
                    min={0}
                    max={1.0}
                    step={0.025}
                    onChange={handleChange}
                    valueLabelDisplay="auto"
                    marks
                    aria-labelledby="smooth-roundness-slider"
                  />
                </FormControl>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={reset}>Cancel</Button>
            <Button variant="contained" type="submit">Save</Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}
